import Link from "next/link";
import { memo, useCallback, useState } from "react";
import { useCopyToClipboard } from "usehooks-ts";
import { useChatVisibility } from "@/hooks/use-chat-visibility";
import type { Chat } from "@/lib/db/schema";
import {
  CheckCircleFillIcon,
  CopyIcon,
  GlobeIcon,
  LockIcon,
  MoreHorizontalIcon,
  ShareIcon,
  TrashIcon,
} from "./icons";
import { toast } from "./toast";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import {
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
} from "./ui/sidebar";

// X (Twitter) icon for sidebar
function XIcon({ size = 12 }: { size?: number }) {
  return (
    <svg
      fill="currentColor"
      height={size}
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

// Telegram icon for sidebar
function TelegramIcon({ size = 12 }: { size?: number }) {
  return (
    <svg
      fill="currentColor"
      height={size}
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
    </svg>
  );
}

type ShortLinkResponse = {
  shortUrl: string;
  code: string;
  title: string;
  isExisting?: boolean;
  error?: string;
};

const PureChatItem = ({
  chat,
  isActive,
  onDelete,
  setOpenMobile,
}: {
  chat: Chat;
  isActive: boolean;
  onDelete: (chatId: string) => void;
  setOpenMobile: (open: boolean) => void;
}) => {
  const { visibilityType, setVisibilityType } = useChatVisibility({
    chatId: chat.id,
    initialVisibilityType: chat.visibility,
  });
  const [, copyToClipboard] = useCopyToClipboard();
  const [copied, setCopied] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Get or create a short link for the chat (also sets to public)
  const getShortLink =
    useCallback(async (): Promise<ShortLinkResponse | null> => {
      try {
        const response = await fetch("/api/short-links", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            targetType: "chat",
            targetId: chat.id,
            setPublic: true, // Auto-set chat to public when sharing
          }),
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.error || "Failed to create share link");
        }

        // Update local visibility state to reflect the change
        setVisibilityType("public");

        return await response.json();
      } catch (error) {
        console.error("Failed to get short link:", error);
        return null;
      }
    }, [chat.id, setVisibilityType]);

  const trackShare = async (platform: "x" | "telegram" | "copy") => {
    try {
      await fetch("/api/analytics/share", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ chatId: chat.id, platform }),
      });
    } catch {
      // Silent fail
    }
  };

  const handleCopyLink = async () => {
    setIsLoading(true);
    try {
      const linkData = await getShortLink();
      if (!linkData) {
        toast({ type: "error", description: "Failed to create share link" });
        return;
      }

      await trackShare("copy");
      await copyToClipboard(linkData.shortUrl);
      setCopied(true);
      toast({ type: "success", description: "Link copied to clipboard" });
      setTimeout(() => setCopied(false), 2000);
    } finally {
      setIsLoading(false);
    }
  };

  const handleXShare = async () => {
    setIsLoading(true);
    try {
      const linkData = await getShortLink();
      if (!linkData) {
        toast({ type: "error", description: "Failed to create share link" });
        return;
      }

      await trackShare("x");
      const shareTitle = linkData.title || chat.title;
      const text = encodeURIComponent(
        `${shareTitle} - Check out this blockchain analysis on TXGPT`
      );
      const url = encodeURIComponent(linkData.shortUrl);
      window.open(
        `https://twitter.com/intent/tweet?text=${text}&url=${url}`,
        "_blank",
        "noopener,noreferrer"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleTelegramShare = async () => {
    setIsLoading(true);
    try {
      const linkData = await getShortLink();
      if (!linkData) {
        toast({ type: "error", description: "Failed to create share link" });
        return;
      }

      await trackShare("telegram");
      const shareTitle = linkData.title || chat.title;
      const text = encodeURIComponent(
        `${shareTitle} - Check out this blockchain analysis on TXGPT`
      );
      const url = encodeURIComponent(linkData.shortUrl);
      window.open(
        `https://t.me/share/url?url=${url}&text=${text}`,
        "_blank",
        "noopener,noreferrer"
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <SidebarMenuItem>
      <SidebarMenuButton asChild isActive={isActive}>
        <Link href={`/chat/${chat.id}`} onClick={() => setOpenMobile(false)}>
          <span>{chat.title}</span>
        </Link>
      </SidebarMenuButton>

      <DropdownMenu modal={true}>
        <DropdownMenuTrigger asChild>
          <SidebarMenuAction
            className="mr-0.5 data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            showOnHover={!isActive}
          >
            <MoreHorizontalIcon />
            <span className="sr-only">More</span>
          </SidebarMenuAction>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="end" side="bottom">
          <DropdownMenuSub>
            <DropdownMenuSubTrigger className="cursor-pointer">
              <ShareIcon />
              <span>Share</span>
            </DropdownMenuSubTrigger>
            <DropdownMenuPortal>
              <DropdownMenuSubContent>
                <DropdownMenuItem
                  className="cursor-pointer flex-row justify-between"
                  onClick={() => {
                    setVisibilityType("private");
                  }}
                >
                  <div className="flex flex-row items-center gap-2">
                    <LockIcon size={12} />
                    <span>Private</span>
                  </div>
                  {visibilityType === "private" ? (
                    <CheckCircleFillIcon />
                  ) : null}
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="cursor-pointer flex-row justify-between"
                  onClick={() => {
                    setVisibilityType("public");
                  }}
                >
                  <div className="flex flex-row items-center gap-2">
                    <GlobeIcon />
                    <span>Public</span>
                  </div>
                  {visibilityType === "public" ? <CheckCircleFillIcon /> : null}
                </DropdownMenuItem>

                <DropdownMenuSeparator />

                <DropdownMenuItem
                  className="cursor-pointer"
                  disabled={isLoading}
                  onClick={handleCopyLink}
                >
                  <div className="flex flex-row items-center gap-2">
                    {copied ? (
                      <CheckCircleFillIcon size={12} />
                    ) : (
                      <CopyIcon size={12} />
                    )}
                    <span>{copied ? "Copied!" : "Copy Link"}</span>
                  </div>
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="cursor-pointer"
                  disabled={isLoading}
                  onClick={handleXShare}
                >
                  <div className="flex flex-row items-center gap-2">
                    <XIcon size={12} />
                    <span>Share on X</span>
                  </div>
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="cursor-pointer"
                  disabled={isLoading}
                  onClick={handleTelegramShare}
                >
                  <div className="flex flex-row items-center gap-2">
                    <TelegramIcon size={12} />
                    <span>Share on Telegram</span>
                  </div>
                </DropdownMenuItem>
              </DropdownMenuSubContent>
            </DropdownMenuPortal>
          </DropdownMenuSub>

          <DropdownMenuItem
            className="cursor-pointer text-destructive focus:bg-destructive/15 focus:text-destructive"
            onSelect={() => onDelete(chat.id)}
          >
            <TrashIcon />
            <span>Delete</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </SidebarMenuItem>
  );
};

export const ChatItem = memo(PureChatItem, (prevProps, nextProps) => {
  if (prevProps.isActive !== nextProps.isActive) {
    return false;
  }
  return true;
});
